'use client';

import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  TooltipItem,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { dimensions, industryAverageData } from '../data/benchmarks';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

interface SpiderChartProps {
  answers?: number[];
  width?: number;
  height?: number;
  /**
   * Light-background, high-contrast theme for PDF/html2canvas capture.
   * Default chart styling assumes a dark panel (white text); without this, labels/grid vanish on white.
   */
  forPdfExport?: boolean;
}

export default function SpiderChart({ answers, width, height, forPdfExport = false }: SpiderChartProps) {
  // Convert answer indices (0-3) to percentages (0-100)
  const calculateScore = (answerIndex: number) => {
    const scoreMap = {
      0: 25, // Basic level
      1: 50, // Intermediate level
      2: 75, // Advanced level
      3: 100, // Expert level
    };
    return scoreMap[answerIndex as keyof typeof scoreMap] || 0;
  };

  const data: ChartData<'radar'> = {
    labels: dimensions,
    datasets: [
      {
        label: 'Industry Average',
        data: industryAverageData,
        backgroundColor: 'rgba(248, 86, 64, 0.4)', // Brand red with transparency
        borderColor: 'rgba(248, 86, 64, 0.9)', // More solid brand red
        borderWidth: 3,
        fill: true,
        tension: 0.5,
        pointBackgroundColor: 'rgba(248, 86, 64, 1)', // Solid brand red
        pointBorderColor: 'transparent',
        pointBorderWidth: 0,
        pointHoverBackgroundColor: 'rgba(248, 86, 64, 1)',
        pointHoverBorderColor: 'transparent',
        pointHoverBorderWidth: 0,
        pointRadius: 5,
        pointHoverRadius: 8,
        borderDash: [],
      },
      {
        label: 'Your Score',
        data: answers
          ? dimensions.map((_, index) => calculateScore(answers[index] || 0))
          : dimensions.map(() => 0), // Show 0 if no answers (e.g., on welcome)
        backgroundColor: 'rgba(255, 192, 0, 0.4)', // Brand yellow with transparency (#FFC000)
        borderColor: 'rgba(255, 192, 0, 0.9)', // More solid brand yellow
        borderWidth: 3,
        fill: true,
        tension: 0.5,
        pointBackgroundColor: 'rgba(255, 192, 0, 1)',
        pointBorderColor: 'transparent',
        pointBorderWidth: 0,
        pointHoverBackgroundColor: 'rgba(255, 192, 0, 1)',
        pointHoverBorderColor: 'transparent',
        pointHoverBorderWidth: 0,
        pointRadius: 5,
        pointHoverRadius: 8,
        borderDash: [],
      },
      {
        label: 'Best Practice',
        data: [100, 100, 100, 100, 100, 100, 100], // 100% across all dimensions, including Implementation/Insights
        backgroundColor: 'rgba(89, 185, 193, 0.4)', // Brand green with transparency
        borderColor: 'rgba(89, 185, 193, 0.9)', // More solid brand green
        borderWidth: 3,
        fill: true,
        tension: 0.5,
        pointBackgroundColor: 'rgba(89, 185, 193, 1)',
        pointBorderColor: 'transparent',
        pointBorderWidth: 0,
        pointHoverBackgroundColor: 'rgba(89, 185, 193, 1)',
        pointHoverBorderColor: 'transparent',
        pointHoverBorderWidth: 0,
        pointRadius: 5,
        pointHoverRadius: 8,
      },
    ],
  };

  const labelColor = forPdfExport ? '#0f172a' : 'rgba(255, 255, 255, 0.9)';
  const tickColor = forPdfExport ? '#334155' : 'rgba(255, 255, 255, 0.8)';
  const legendColor = forPdfExport ? '#1e293b' : 'rgba(255, 255, 255, 0.9)';
  const angleLineColor = forPdfExport ? 'rgba(71, 85, 105, 0.55)' : 'rgba(148, 163, 184, 0.2)';
  const ringGridColor = forPdfExport ? 'rgba(100, 116, 139, 0.45)' : 'rgba(148, 163, 184, 0.15)';

  const options: ChartOptions<'radar'> = {
    scales: {
      r: {
        angleLines: {
          display: true,
          color: angleLineColor,
        },
        grid: {
          color: ringGridColor,
          circular: true,
        },
        suggestedMin: 0,
        suggestedMax: 100,
        ticks: {
          callback: function (tickValue: number | string) {
            return `${tickValue}%`;
          },
          backdropColor: forPdfExport ? 'rgba(255,255,255,0.85)' : 'transparent',
          color: tickColor,
          font: {
            size: forPdfExport ? 13 : 12,
            family: "'Inter', system-ui, sans-serif",
            weight: 500,
          },
          stepSize: 25,
          maxTicksLimit: 5,
        },
        pointLabels: {
          font: {
            size: forPdfExport ? 12 : 11,
            family: "'Inter', system-ui, sans-serif",
            weight: 600,
          },
          color: labelColor,
          padding: forPdfExport ? 12 : 10,
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: forPdfExport ? 13 : 12,
            family: "'Inter', system-ui, sans-serif",
            weight: 600,
          },
          color: legendColor,
          padding: 16,
          usePointStyle: true,
          boxWidth: 6,
          boxHeight: 6,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        titleColor: 'rgba(15, 23, 42, 0.9)',
        bodyColor: 'rgba(71, 85, 105, 0.9)',
        borderColor: 'rgba(226, 232, 240, 0.8)',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        titleFont: {
          size: 14,
          weight: 600,
          family: "'Inter', system-ui, sans-serif",
        },
        bodyFont: {
          size: 13,
          family: "'Inter', system-ui, sans-serif",
        },
        callbacks: {
          label: function (context: TooltipItem<'radar'>) {
            return `${context.dataset.label}: ${Math.round(context.raw as number)}%`;
          },
        },
      },
    },
    animation: {
      duration: 0,
    },
    maintainAspectRatio: false,
    elements: {
      line: {
        borderWidth: 3,
        tension: 0.5,
      },
    },
  };

  return (
    <div
      className="spider-chart-container"
      style={{
        width: width || '100%',
        height: height || '100%',
      }}
    >
      <Radar data={data} options={options} />
    </div>
  );
}
